import { useState, useCallback } from 'react';
import * as api from '../services/api';

/**
 * Hook for managing users (admin only)
 */
export function useAdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  /**
   * Fetch users with optional filters
   */
  const fetchUsers = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError(null);

      const result = await api.fetchAdminUsers(options);
      
      setUsers(result.users || []);
      setPagination(result.pagination || null);

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch single user details
   */
  const fetchUser = useCallback(async (userId) => {
    try {
      setError(null);
      const result = await api.fetchAdminUser(userId);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update user status (active/disabled)
   */
  const updateUserStatus = useCallback(async (userId, status) => {
    try {
      setError(null);
      const result = await api.updateUserStatus(userId, status);
      
      // Update the user in state
      setUsers((prev) => 
        prev.map((user) => (user.id === userId ? result.user : user))
      );
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update user admin status (promote/demote)
   */
  const updateUserAdmin = useCallback(async (userId, isAdmin) => {
    try {
      setError(null);
      const result = await api.updateUserAdmin(userId, isAdmin);
      
      // Update the user in state
      setUsers((prev) => 
        prev.map((user) => (user.id === userId ? result.user : user))
      );
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update user subscription tier
   */
  const updateUserTier = useCallback(async (userId, tierId) => {
    try {
      setError(null);
      const result = await api.updateUserTier(userId, tierId);
      
      // Update the user in state
      setUsers((prev) => 
        prev.map((user) => (user.id === userId ? result.user : user))
      );
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete a user (hard delete)
   */
  const deleteUser = useCallback(async (userId) => {
    try {
      setError(null);
      await api.deleteUser(userId);
      
      // Remove the user from state
      setUsers((prev) => prev.filter((user) => user.id !== userId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    users,
    loading,
    error,
    pagination,
    fetchUsers,
    fetchUser,
    updateUserStatus,
    updateUserAdmin,
    updateUserTier,
    deleteUser,
  };
}
